<template lang="pug">
div
	portal(to="full-width" :disabled="this.$vuetify.breakpoint.smAndDown" )
		h1 {{trip.name}}
		p {{trip.introduction}}
		.font-weight-bold Waarom naar {{trip.destination.name}}?
		.mb-6
			v-row(dense v-for="point in trip.bulletpoints" :key="`${trip.id}${point}`")
				v-col.shrink
					ws-icon(color="success" small) check
				v-col {{point}}

		h2 Boek deze wintersportvakantie in Nederland
		v-row.mb-6
			v-col(cols="12" md="6")
				v-card.flex.d-flex.flex-column(height="100%" outlined)
					div
						v-img(:src="`${trip.locationPhoto}?width=600&quality=90`" :aspect-ratio="16/9")
					v-card-text.flex.pb-0
						h3(v-if="trip.destination.name.startsWith('Langlauf')") Langlaufen bij {{trip.destination.name}}
						h3(v-else) Skiën bij {{trip.destination.name}}
						p {{trip.locationText}}
					v-card-text
						.text-center
							v-btn(:href="trip.locationLink" target="_blank" rel="nofollow" color="primary" depressed) koop je e-ticket
			v-col(cols="12" md="6")
				v-card.flex.d-flex.flex-column(height="100%" outlined)
					div
						v-img(:src="`${trip.hotelPhoto}?width=600&quality=90`" :aspect-ratio="16/9" v-ripple)
					v-card-text.flex.pb-0
						h3 {{trip.hotelTitle}}
						p {{trip.hotelText}}
					v-card-text
						.text-center
							v-btn(:href="trip.hotelLink" target="_blank" rel="nofollow" color="primary" depressed) bekijk accommodatie

	portal(to="sidebar" :disabled="this.$vuetify.breakpoint.smAndDown" :order="1")
		v-card.mb-6(outlined)
			v-card-text
				h2.font-weight-bold.text-h6 {{trip.destination.name}}
				v-list.factsheet(subheader)
					v-list-item.px-0(v-for="fact in facts" dense :key="fact.label")
						v-list-item-avatar.mr-2
							ws-icon {{fact.icon}}
						v-list-item-content.py-2
							v-list-item-title.caption.font-weight-bold {{fact.label}}
							v-list-item-subtitle.caption.black--text {{fact.display ? fact.display : fact.value}}
				.font-weight-bold Locatie
				address-display(:address="trip.destination.address")
				v-divider.my-3
				.text-center
					v-btn(:to="localePath({name:`localdestinations-${trip.destination.__typename.toLowerCase()}s-slug`, params: {slug: trip.destination.slug}})" color="primary" outlined) uitgebreide informatie

	portal(to="sidebar" :disabled="this.$vuetify.breakpoint.smAndDown" :order="2")
		v-card.mb-6(color="info" outlined)
			v-card-text
				h2.text-h6.font-weight-bold Wat je nog moet weten over jouw wintersportvakantie in Nederland
				p Wintersport in Nederland is een initiatief van de Nederlandse Ski Vereniging / Wintersport.nl. Er zijn in Nederland vele mogelijkheden om te wintersporten op echte sneeuw, rolbanen en outdoor mattenbanen. Soms binnen in een hal, soms met uitzicht op typisch Nederlandse landschappen.
				p Op deze pagina geven we je een suggestie van enkele activiteiten vlakbij een accommodatie. Ook hebben we een aantal accommodaties voor je geselecteerd, zodat je er echt een wintersportvakantie van kunt maken. Boeken doe je altijd zelf direct bij zowel de locatie als bij de accommodatie. Wij geven je de inspiratie, jij regelt de rest.
				p Met jouw bezoek aan een wintersportlocatie in Nederland en het boeken van een accommodatie nadat je bent doorgestuurd naar een boekingsplatform ondersteun je de wintersport in Nederland.
	.mb-6
		h2 Activiteiten
		ws-content(:value="trip.activitiesText" :markdown="trip.activitiesText.markdownl3")
	.mb-6
		h2 Overnachten
		v-row
			v-col(cols="12" md="6" v-if="trip.hotelLink")
				v-card(height="100%" outlined)
					v-img(:src="`${trip.hotelPhoto}?width=600&quality=90`" :aspect-ratio="16/9")
					v-card-text
						h3 {{trip.hotelTitle}}
						p {{trip.hotelText}}
						.text-center
							v-btn(:href="trip.hotelLink" target="_blank" rel="nofollow" color="primary" depressed) regel je overnachting
			v-col(cols="12" md="6" v-if="trip.resortLink")
				v-card(height="100%" outlined)
					v-img(:src="`${trip.resortPhoto}?width=600&quality=90`" :aspect-ratio="16/9")
					v-card-text
						h3 {{trip.resortTitle}}
						p {{trip.resortText}}
						.text-center
							v-btn(:href="trip.resortLink" target="_blank" rel="nofollow" color="primary" depressed) bekijk alle opties


	portal(to="sidebar" :disabled="this.$vuetify.breakpoint.smAndDown" :order="3")
		ad(location="sidebar")

	.mb-6(v-intersect.once="{ handler: loadRelatedTrips }")
		h2 Meer wintersport in Nederland
		v-row
			v-col(cols="12" md="6" v-for="relatedTrip in displayRelatedTrips" :key="relatedTrip.id")
				trip-card(v-bind="relatedTrip")

	portal(to="sidebar" :disabled="this.$vuetify.breakpoint.smAndDown" :order="4")
		offers.mb-6

</template>
<script>
import gql from 'graphql-tag';
import Content from '@dengl/common/components/content.vue';
import MetaFragment from '@/components/graphql/metaFragment.graphql';
import TripCard from '@/components/trips/card.vue';
import AddressDisplay from '@/components/localdestinations/address';

const query = gql`
	query getTrip($slug: String) {
		trip(slug: $slug) {
			id
			name
			slug
			image
			introduction
			locationText
			locationLink
			locationPhoto
			hotelTitle
			hotelText
			hotelLink
			hotelPhoto
			resortTitle
			resortText
			resortLink
			resortPhoto
			tags
			activitiesText {
				...ContentDataFragment
				markdownl3: markdown(level: 3)
			}
			bulletpoints
			destination {
				id
				slug
				name
				... on Skihall {
					longestPisteLength
					chairlift
					tbar
					magiccarpet
				}
				... on Dryslope {
					longestPisteLength
					loipeLength
					tbar
					magiccarpet
				}
				... on Revolvingslope {
					hasAdjustableSlopes
					maxPersonsOnSlope
					numberOfSlopes
					maxSlopeWidth
					maxSlopeLength
				}
				address {
					city
					street
					phone
					postcode
					country
					url
					countrycode
				}
				mainImage
			}
			...MetaFragment
		}
	}
	${MetaFragment}
	${Content.fragments}
`;

export default {
	name: 'TripItem',
	nuxtI18n: {
		paths: {
			'nl-NL': '/wintersport-in-nederland/:slug',
			'nl-BE': '/wintersport-in-nederland/:slug'
		}
	},
	components: { TripCard, AddressDisplay },
	asyncData({ app, params }) {
		return app.$graphClient
			.query({
				query,
				variables: {
					slug: params.slug
				},
				errorPolicy: 'all'
			})
			.then(({ data, errors }) => {
				if (!data.trip) {
					return app.httpError(404, null, 'trips-slug-01', errors);
				}
				if (errors) {
					throw errors[0];
				}

				app.meta(data.trip.meta);
				app.ads({
					sidebar: { visible: true }
				});
				app.breadcrumbs({
					'Wintersport in Nederland': {
						name: 'trips'
					},
					[data.trip.name]: {
						name: 'trips-slug',
						params: { slug: data.trip.slug }
					}
				});

				return data;
			})
			.catch(error => {
				return app.httpError(500, null, 'trips-slug-02', error);
			});
	},
	data() {
		return { relatedTrips: [] };
	},
	head() {
		return {
			...this.$structuredData()
		};
	},
	computed: {
		displayRelatedTrips() {
			return this.relatedTrips
				.filter(trip => trip.id !== this.trip.id)
				.slice(0, 2);
		},
		facts() {
			return [
				{
					value: this.trip.destination.longestPisteLength,
					icon: 'distance',
					label: 'Langste afdaling',
					display: `${this.trip.destination.longestPisteLength}m`
				},
				{
					icon: 'langlauf',
					label: 'Langlaufloipen',
					display: `${this.trip.destination.loipeLength}m`,
					value: this.trip.destination.loipeLength
				},
				{
					value: this.trip.destination.chairlift,
					icon: 'chair-lift',
					label: 'Stoeltjesliften'
				},
				{
					value: this.trip.destination.tbar,
					icon: 't-bar',
					label: 'Sleepliften'
				},
				{
					value: this.trip.destination.magiccarpet,
					icon: 'conveyor',
					label: 'Lopende banden'
				},
				{
					value: this.trip.destination.numberOfSlopes,
					label: 'Aantal rolbanen',
					icon: 'carpetslope'
				},
				{
					value: this.trip.destination.maxSlopeWidth,
					label: 'Breedste rolbaan',
					icon: 'distance',
					display: `${this.trip.destination.maxSlopeWidth}m`
				},
				{
					value: this.trip.destination.maxSlopeLength,
					label: 'Langste rolbaan',
					icon: 'distance',
					display: `${this.trip.destination.maxSlopeLength}m`
				},
				{
					value: this.trip.destination.maxPersonsOnSlope,
					label: 'Max pers. per baan',
					icon: 'ski'
				},
				{
					value: this.trip.destination.hasAdjustableSlopes,
					label: 'Verstelbare banen',
					icon: 'height',
					display: this.trip.destination.hasAdjustableSlopes ? 'Ja' : 'Nee'
				}
			].filter(fact => fact.value);
		}
	},
	methods: {
		loadRelatedTrips() {
			this.$graphClient
				.query({
					query: gql`
						query tripsByTag($limit: Int, $tags: [String]) {
							tripsByTag(limit: $limit, tags: $tags) {
								id
								name
								slug
								image: locationPhoto
								introduction
								bulletpoints
								tags
								province
								destination {
									id
									slug
									name
								}
							}
						}
					`,
					variables: {
						tags: this.trip.tags,
						limit: 3
					},
					errorPolicy: 'all'
				})
				.then(({ data, _errors }) => {
					this.relatedTrips = data.tripsByTag;
				});
		}
	}
};
</script>
